// AUTO-EXTRACTED from mockups/science-studies-landing.html, section
// `#questions`, on 2026-08-25. Data only - presentation lives in the
// components beside this file.
//
// The item noun is a QUESTION, not an answer: a card is not our answer to
// something the reader asked, it is a question put to our data. Counters read
// "13 questions" and the back link reads "All questions".

export type AskCategoryId =
  | 'heart'
  | 'sleep'
  | 'metabolic'
  | 'women'
  | 'environment';

export interface AskQuestion {
  slug: string;
  /** Authored rail order. Not alphabetical, not by cohort size. */
  order: number;
  /** The question's topic in two or three words, for the breadcrumb.
   *
   *  Authored rather than derived. It cannot be the subject — that is already
   *  the pill directly below the crumb, and a trail that repeats the tag tells
   *  the reader nothing. It cannot be the question either: a question is a
   *  sentence, and truncating one mid-clause reads as a broken link. And it
   *  cannot come off the slug, because half of these are proper nouns the
   *  slug flattens (apob -> ApoB, hba1c -> HbA1c, lpa -> Lp(a)). */
  shortLabel: string;
  /** First entry is the PRIMARY subject: it sets the pill and the art hue. */
  categories: AskCategoryId[];
  question: string;
  /** The short answer on the card. Observational - an association, never a cause. */
  answer: string;
  /** Cohort size, already formatted ("4,841 members"). */
  cohort: string;
  /** What the figure was measured with. */
  reference: string;
  /** Art direction for the photograph that will replace the placeholder.
      Never rendered - it is a brief for a photographer, not alt text. */
  artBrief: string;
  /* Card art. A CDN URL, served from Asset Studio.
   *
   * These now live in Asset Studio under `science/ask/<slug>.jpg`, in the
   * "Science / Ask Our Data" folder, so the bytes are replaceable by design
   * without a deploy - upload a new file against the same key in Anton and
   * this URL serves it. The `?v=` token on the end is Asset Studio's own
   * cache-buster; it changes when the bytes do, so leave it on.
   *
   * Deliberately a full URL rather than a slug plus a helper: when the section
   * becomes backend-driven the value becomes whatever `cover_image_url` holds, and a
   * helper that assumed a local prefix would have to be unpicked. A string
   * that is already the whole address changes host without changing shape. */
  coverImageUrl?: string;
  coverImageAlt?: string;
}

export const ASK_CATEGORY_LABEL: Record<AskCategoryId, string> = {
  heart: 'Heart health',
  sleep: 'Sleep',
  metabolic: 'Metabolic health',
  women: "Women's health",
  environment: 'Environment',
};

/* Every card is interactive: each figure carries a plate where the reader
   types a number of their own and the figure answers it. There is no
   non-interactive variant today, so it is a property of the section rather
   than a per-card flag. */
export const ASK_QUESTIONS: AskQuestion[] = [
  {
    slug: 'apob',
    shortLabel: 'ApoB',
    order: 1,
    categories: ['heart'],
    question: 'Is your cholesterol test missing a number?',
    answer: 'There\'s a second cholesterol number, and standard panels skip it. Nearly a third of members with normal LDL had it raised.',
    cohort: '4,841 members',
    reference: 'a laboratory blood panel',
    artBrief: 'Someone in their thirties at a kitchen table in morning light, holding a folded lab report they have just read. Graded deep oxblood. Face lit; the report itself unreadable.',
    coverImageUrl:
      'https://aicdn.speedsize.com/3f711f28-1488-44dc-b013-5e43284ac4b0/https://public-assets.uh-static.com/science/ask/apob.jpg?speedsize=hh_2688,r_inside&v=1',
  },
  {
    slug: 'hot-flashes-and-sleep',
    shortLabel: 'Hot flushes',
    order: 2,
    categories: ['women'],
    question: 'Do hot flushes wake you up at night?',
    answer: 'A flush night is no shorter than her others, but it trades 3.79 minutes of REM and 3.01 of deep sleep for time lying awake.',
    cohort: '809 members',
    reference: 'the Ultrahuman Ring',
    artBrief: 'A woman in her late forties sitting up in bed in the small hours, sheet pushed back, one hand at the back of her neck. Graded warm plum, window light behind her.',
    coverImageUrl:
      'https://aicdn.speedsize.com/3f711f28-1488-44dc-b013-5e43284ac4b0/https://public-assets.uh-static.com/science/ask/hotflush.jpg?speedsize=hh_2688,r_inside&v=1',
  },
  {
    slug: 'social-jetlag',
    shortLabel: 'Social jetlag',
    order: 3,
    categories: ['sleep', 'heart'],
    question: 'What does sleeping in at the weekend do to your heart?',
    answer: 'It\'s Members whose sleep drifted 24 to 34 minutes later at the weekend ran up to 0.66 bpm higher overnight the next morning.',
    cohort: '187,796 members',
    reference: 'the Ultrahuman Ring',
    artBrief: 'Someone on the edge of an unmade bed on a Sunday morning, late light through the curtains, not yet standing. Graded cold slate blue.',
    coverImageUrl:
      'https://aicdn.speedsize.com/3f711f28-1488-44dc-b013-5e43284ac4b0/https://public-assets.uh-static.com/science/ask/socialjetlag.jpg?speedsize=hh_2688,r_inside&v=1',
  },
  {
    slug: 'resting-heart-rate-by-age',
    shortLabel: 'Resting heart rate',
    order: 4,
    categories: ['heart'],
    question: 'Is your resting heart rate normal for your age?',
    answer: '65.2% of 231,506 members sleep below the clinic’s 60 bpm line. See where yours sits for your age and sex, and when a high one deserves a look.',
    cohort: '231,506 members',
    reference: 'the Ultrahuman Ring',
    artBrief: 'A woman in her forties asleep on her side in the last hour before dawn, duvet risen just past her shoulder, one hand resting loose on the pillow near her collarbone. The room is graded hard to deep oxblood shadow, a single cool window glow raking across her face and the fold of the sheet. Everything is still — the picture is a heart idling at its slowest hour of the day.',
    coverImageUrl:
      'https://aicdn.speedsize.com/3f711f28-1488-44dc-b013-5e43284ac4b0/https://public-assets.uh-static.com/science/ask/percentiles.jpg?speedsize=hh_2688,r_inside&v=1',
  },
  {
    slug: 'sauna-and-sleep',
    shortLabel: 'Sauna',
    order: 5,
    categories: ['sleep'],
    question: 'What does a hot sauna do to your sleep?',
    answer: 'Sleep scores rose 1.48 points on sauna nights, and deep sleep fell 3.58 minutes.',
    cohort: '180 members',
    reference: 'the Ultrahuman Ring',
    artBrief: 'A woman in her forties sits alone on the cedar bench of a small home sauna, towel over her shoulders, eyes closed, head tipped back against the slats as the evening\'s heat settles out of her. The only light is the blue of late dusk through one small window, cooling the warm wood hard toward a cold slate blue. Sweat catches that thin light along her collarbone — the exhale before bed.',
    coverImageUrl:
      'https://aicdn.speedsize.com/3f711f28-1488-44dc-b013-5e43284ac4b0/https://public-assets.uh-static.com/science/ask/sauna.jpg?speedsize=hh_2688,r_inside&v=1',
  },
  {
    slug: 'hba1c',
    shortLabel: 'HbA1c',
    order: 6,
    categories: ['metabolic'],
    question: 'Is your normal blood-sugar test hiding something?',
    answer: 'Four in ten members with a normal result still spike past 140 mg/dL on an average day.',
    cohort: '1,596 members',
    reference: 'a laboratory blood panel and a continuous glucose monitor',
    artBrief: 'A man in his forties at a kitchen counter on an ordinary weekday morning, one hand on a half-finished pastry, the other lifting a glass of orange juice, mid-bite and unposed. Low window light rakes across the counter and catches the juice, the whole frame graded hard into warm amber so the glass glows like the hottest point in the image. Everything about the moment reads as routine, which is the point.',
    coverImageUrl:
      'https://aicdn.speedsize.com/3f711f28-1488-44dc-b013-5e43284ac4b0/https://public-assets.uh-static.com/science/ask/hba1c.jpg?speedsize=hh_2688,r_inside&v=1',
  },
  {
    slug: 'recovery-before-your-period',
    shortLabel: 'Cycle recovery',
    order: 7,
    categories: ['women'],
    question: 'Why does your recovery dip before your period?',
    answer: 'Around a logged cramps day, overnight HRV climbs 3 ms and resting heart rate falls 2.7 bpm — and the cramp itself is worth less than 1 bpm of it.',
    cohort: '19,910 members',
    reference: 'the Ultrahuman Ring',
    artBrief: 'a woman curled on her side in bed at first light, hot water bottle at her stomach, duvet half thrown off, the window\'s raking light crossing the sheets. Subject off-centre low-right with headroom top-left; graded hard to a warm plum; no chart, no product, no ring in frame.',
    coverImageUrl:
      'https://aicdn.speedsize.com/3f711f28-1488-44dc-b013-5e43284ac4b0/https://public-assets.uh-static.com/science/ask/cycle.jpg?speedsize=hh_2688,r_inside&v=1',
  },
  {
    slug: 'bedroom-air-and-sleep',
    shortLabel: 'Bedroom air',
    order: 8,
    categories: ['environment', 'sleep'],
    question: 'Is your bedroom air changing how you sleep?',
    answer: 'On the stuffiest and most humid nights, deep sleep’s share ran about a third of a point lower — and ordinary noise hardly mattered.',
    cohort: '1,242 members',
    reference: 'the Ultrahuman Home and the Ultrahuman Ring',
    artBrief: 'A woman in a sleep shirt pushes a bedroom sash window open last thing at night, one hand on the frame, curtain lifting slightly in the incoming air. The room behind her is dim and lived-in — rumpled duvet, a glass of water on the sill — lit only by a warm bedside lamp against the blue-black dark outside. Graded hard to deep green, the lamp\'s glow doing all the work.',
    coverImageUrl:
      'https://aicdn.speedsize.com/3f711f28-1488-44dc-b013-5e43284ac4b0/https://public-assets.uh-static.com/science/ask/bedroomair.jpg?speedsize=hh_2688,r_inside&v=1',
  },
  {
    slug: 'irregular-bedtime-heart-rate',
    shortLabel: 'Bedtime',
    order: 9,
    categories: ['sleep', 'heart'],
    question: 'Does an irregular bedtime raise your heart rate?',
    answer: 'It shows up, a little. The bigger deal is sleep that keeps changing length: those members ran 5.6 to 7.1 bpm higher, in every age band.',
    cohort: '103,217 members',
    reference: 'the Ultrahuman Ring',
    artBrief: 'A person mid-30s sitting up in bed in the near-dark, one hand silencing an alarm clock that reads an odd hour, duvet half thrown back. The only light is a thin pre-dawn seam through the curtains, raking across the pillow and the tangle of sheets from a night that ran short. Graded hard to a cold slate blue; the room stays in shadow, the face half-lit and unposed.',
    coverImageUrl:
      'https://aicdn.speedsize.com/3f711f28-1488-44dc-b013-5e43284ac4b0/https://public-assets.uh-static.com/science/ask/bedtime.jpg?speedsize=hh_2688,r_inside&v=1',
  },
  {
    slug: 'lpa',
    shortLabel: 'Lp(a)',
    order: 10,
    categories: ['heart'],
    question: 'Is a hidden heart risk running in your family?',
    answer: 'It\'s called Lp(a), and nearly one in five members have it raised. Most of them had a clean cholesterol test.',
    cohort: '2,473 members',
    reference: 'a laboratory blood panel',
    artBrief: 'A man in his forties sits alone at the kitchen table before the house wakes, holding a creased photograph of his late father at the same age. Early window light rakes across the table and catches the edge of the print; the rest of the room falls into shadow. Graded hard to deep oxblood; no chart, no product, no ring in frame, no clinic.',
    coverImageUrl:
      'https://aicdn.speedsize.com/3f711f28-1488-44dc-b013-5e43284ac4b0/https://public-assets.uh-static.com/science/ask/lpa.jpg?speedsize=hh_2688,r_inside&v=1',
  },
  {
    slug: 'magnesium-and-sleep',
    shortLabel: 'Magnesium',
    order: 11,
    categories: ['sleep'],
    question: 'Does magnesium actually improve your sleep?',
    answer: 'On magnesium nights the poorest sleepers spent 4.39 fewer minutes in light sleep and 3.42 fewer awake, on a night no longer than usual. The worse your nights, the bigger the difference.',
    cohort: '343 members',
    reference: 'the Ultrahuman Ring',
    artBrief: 'A woman at her bathroom sink at the very end of the day, tipping a capsule from a jar into her palm, a glass of water waiting on the porcelain edge. The room is lit only by a cold spill of landing light through the half-open door, catching her wrist and the water. Graded hard to a cold slate blue, shadows deep, everything else already asleep. No chart, no product, no ring in frame.',
    coverImageUrl:
      'https://aicdn.speedsize.com/3f711f28-1488-44dc-b013-5e43284ac4b0/https://public-assets.uh-static.com/science/ask/magnesium.jpg?speedsize=hh_2688,r_inside&v=1',
  },
  {
    slug: 'blood-sugar-and-sleep',
    shortLabel: 'Glucose at night',
    order: 12,
    categories: ['metabolic', 'sleep'],
    question: 'Does your blood sugar follow you to bed?',
    answer: 'The quarter of members with the most time in a tight glucose band slept with heart rates 8.3 bpm lower than the quarter with the least.',
    cohort: '1,456 members',
    reference: 'the Ultrahuman Ring and a continuous glucose monitor',
    artBrief: 'A woman asleep on her side in the last of the evening light, one hand loose on the pillow, a half-finished glass of water and the remains of a late dinner just visible on the bedside table. The room falls away into warm shadow. Graded hard to warm amber, a low raking shaft from a street-facing window doing all the work.',
    coverImageUrl:
      'https://aicdn.speedsize.com/3f711f28-1488-44dc-b013-5e43284ac4b0/https://public-assets.uh-static.com/science/ask/glucosenight.jpg?speedsize=hh_2688,r_inside&v=1',
  },
  {
    slug: 'inflammation-and-sleeping-heart-rate',
    shortLabel: 'Inflammation',
    order: 13,
    categories: ['heart'],
    question: 'Does inflammation show up in your sleeping pulse?',
    answer: 'Members with the highest inflammation ran 4.3 bpm faster in their sleep.',
    cohort: '2,788 members',
    reference: 'a laboratory blood panel and the Ultrahuman Ring',
    artBrief: 'A person asleep on their side in the small hours, one hand resting loose on the pillow, the duvet rising and falling almost imperceptibly. A streetlamp through a gap in the curtains lays one raking shaft across the bed; everything else falls into shadow. Graded hard to deep oxblood, the darks swallowing the edges of the frame.',
    coverImageUrl:
      'https://aicdn.speedsize.com/3f711f28-1488-44dc-b013-5e43284ac4b0/https://public-assets.uh-static.com/science/ask/inflammation.jpg?speedsize=hh_2688,r_inside&v=1',
  },
];

export default ASK_QUESTIONS;
